import { error } from '@sveltejs/kit';
import type { Component } from 'svelte';

export type Guide = {
	title: string;
	slug: string;
	date: string;
	description: string;
	tags: string[];
	published: boolean;
};

type GuideModule = {
	default: Component;
	metadata: Guide;
};

type GuideRecord = Guide & {
	formattedDate: string;
	component?: Component;
};

const guideModules = import.meta.glob('/src/content/guides/*.md', { eager: true }) as Record<
	string,
	GuideModule
>;

function formatDate(date: string) {
	return new Intl.DateTimeFormat('en', {
		day: 'numeric',
		month: 'long',
		year: 'numeric'
	}).format(new Date(date));
}

function sortGuides(guides: Guide[]) {
	return guides.sort((left, right) => right.date.localeCompare(left.date));
}

export function getGuides(): GuideRecord[] {
	return sortGuides(
		Object.values(guideModules)
			.map(({ metadata }) => metadata)
			.filter((guide) => guide.published)
	).map((guide) => ({
		...guide,
		formattedDate: formatDate(guide.date)
	}));
}

export function getGuide(slug: string): GuideRecord {
	const match = Object.values(guideModules).find(({ metadata }) => metadata.slug === slug);

	if (!match || !match.metadata.published) {
		throw error(404, 'Guide not found');
	}

	return {
		...match.metadata,
		formattedDate: formatDate(match.metadata.date),
		component: match.default
	};
}
